import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { isTokenExpired } from '../lib/jwt'
import { onSessionExpired } from './sessionExpired'

type WalletType = 'phantom' | 'solflare' | null

interface AuthState {
  portfolioId: string | null
  token: string | null
  refreshToken: string | null
  walletType: WalletType
  isAuthenticated: boolean
}

interface AuthContextValue extends AuthState {
  login: (portfolioId: string, token: string, refreshToken: string | null, walletType?: WalletType) => void
  logout: () => void
  updateTokens: (accessToken: string, refreshToken: string) => void
  /** Set only when logout happened because the backend rejected the session
   *  (expired/invalid/revoked) — not on an explicit user-initiated
   *  disconnect. WelcomePage shows this so the sudden return to the login
   *  screen doesn't look like the app silently lost the user's data. */
  sessionExpiredMessage: string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const token = sessionStorage.getItem('jwt')
    const portfolioId = sessionStorage.getItem('portfolioId')
    const refreshToken = sessionStorage.getItem('refreshJwt')
    const walletType = (sessionStorage.getItem('walletType') as WalletType) || null
    if (token && portfolioId && !isTokenExpired(token)) {
      return { portfolioId, token, refreshToken, walletType, isAuthenticated: true }
    }
    sessionStorage.removeItem('jwt')
    sessionStorage.removeItem('portfolioId')
    return { portfolioId: null, token: null, refreshToken, walletType: null, isAuthenticated: false }
  })

  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null)

  // Mirrors state.isAuthenticated for the onSessionExpired handler below,
  // updated synchronously each render (not in an effect) so the handler
  // always sees the latest value even though it only re-subscribes when
  // `logout` changes. Needed to guard against a request that was already
  // in flight when the user hit "Disconnect" resolving afterwards with a
  // 401 and re-triggering session-expired handling for a session that was
  // deliberately, cleanly ended — not one the backend rejected.
  const isAuthenticatedRef = useRef(state.isAuthenticated)
  isAuthenticatedRef.current = state.isAuthenticated

  const login = useCallback((portfolioId: string, token: string, refreshToken: string | null, walletType?: WalletType) => {
    sessionStorage.setItem('jwt', token)
    sessionStorage.setItem('portfolioId', portfolioId)
    if (refreshToken) sessionStorage.setItem('refreshJwt', refreshToken)
    if (walletType) sessionStorage.setItem('walletType', walletType)
    setSessionExpiredMessage(null)
    setState({ portfolioId, token, refreshToken, walletType: walletType ?? null, isAuthenticated: true })
  }, [])

  const logout = useCallback(() => {
    sessionStorage.removeItem('jwt')
    sessionStorage.removeItem('portfolioId')
    sessionStorage.removeItem('refreshJwt')
    sessionStorage.removeItem('walletType')
    setState({ portfolioId: null, token: null, refreshToken: null, walletType: null, isAuthenticated: false })
  }, [])

  const updateTokens = useCallback((accessToken: string, refreshToken: string) => {
    sessionStorage.setItem('jwt', accessToken)
    sessionStorage.setItem('refreshJwt', refreshToken)
    setState(prev => ({ ...prev, token: accessToken, refreshToken }))
  }, [])

  // The backend told us (via apiFetch or the WS refresh path) that this
  // session is no longer valid — log out and show why, so the app doesn't
  // just silently strand the user on a dashboard that can't fetch anything.
  useEffect(() => {
    return onSessionExpired(() => {
      // Already logged out — either the user disconnected deliberately, or
      // an earlier session-expired event already handled this. A stale
      // in-flight request resolving with a 401 after that point isn't a
      // new session ending, it's an echo; don't overwrite a clean
      // disconnect with an inaccurate "your session expired" banner.
      if (!isAuthenticatedRef.current) return
      logout()
      setSessionExpiredMessage('Your session expired. Reconnect your wallet to continue.')
    })
  }, [logout])

  return (
    <AuthContext.Provider value={{ ...state, login, logout, updateTokens, sessionExpiredMessage }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
